import React, { useEffect, useState, useCallback } from 'react';
import { create } from 'zustand';
import { CheckCircle, AlertCircle, AlertTriangle, Info, X } from 'lucide-react';

// =============================================
// Toast Store
// =============================================
type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastMessage {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

interface ToastStore {
  toasts: ToastMessage[];
  addToast: (toast: Omit<ToastMessage, 'id'>) => void;
  removeToast: (id: string) => void;
}

export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  addToast: (toast) =>
    set((state) => ({
      toasts: [
        ...state.toasts,
        { ...toast, id: Date.now().toString() + Math.random().toString(36) },
      ],
    })),
  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),
}));

// Convenience functions
export const toast = {
  success: (title: string, message?: string) =>
    useToastStore.getState().addToast({ type: 'success', title, message }),
  error: (title: string, message?: string) =>
    useToastStore.getState().addToast({ type: 'error', title, message }),
  warning: (title: string, message?: string) =>
    useToastStore.getState().addToast({ type: 'warning', title, message }),
  info: (title: string, message?: string) =>
    useToastStore.getState().addToast({ type: 'info', title, message }),
};

// =============================================
// Toast Item
// =============================================
const iconMap: Record<ToastType, React.ReactNode> = {
  success: <CheckCircle className="w-5 h-5 text-success-500" />,
  error: <AlertCircle className="w-5 h-5 text-error-500" />,
  warning: <AlertTriangle className="w-5 h-5 text-warning-500" />,
  info: <Info className="w-5 h-5 text-info-500" />,
};

const bgMap: Record<ToastType, string> = {
  success: 'bg-success-50 dark:bg-[#1a1a2e] border-success-500/20',
  error: 'bg-error-50 dark:bg-[#1a1a2e] border-error-500/20',
  warning: 'bg-warning-50 dark:bg-[#1a1a2e] border-warning-500/20',
  info: 'bg-info-50 dark:bg-[#1a1a2e] border-info-500/20',
};

const ToastItem: React.FC<{ toast: ToastMessage }> = ({ toast: t }) => {
  const removeToast = useToastStore((s) => s.removeToast);
  const [isExiting, setIsExiting] = useState(false);

  const handleRemove = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => removeToast(t.id), 200);
  }, [removeToast, t.id]);

  useEffect(() => {
    const duration = t.duration || 4000;
    const timer = setTimeout(handleRemove, duration);
    return () => clearTimeout(timer);
  }, [t.duration, handleRemove]);

  return (
    <div
      className={`
        flex items-start gap-3 px-4 py-3 rounded-xl border shadow-md
        transition-all duration-200
        ${bgMap[t.type]}
        ${isExiting ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0 animate-slide-up'}
      `}
    >
      <span className="shrink-0 mt-0.5">{iconMap[t.type]}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">{t.title}</p>
        {t.message && (
          <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-0.5">{t.message}</p>
        )}
      </div>
      <button
        onClick={handleRemove}
        className="shrink-0 p-0.5 rounded text-neutral-400 hover:text-neutral-600 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// =============================================
// Toast Container
// =============================================
export const ToastContainer: React.FC = () => {
  const toasts = useToastStore((s) => s.toasts);

  if (toasts.length === 0) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="false"
      className="fixed top-4 right-4 z-[100] flex flex-col gap-2 w-full max-w-sm"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} />
      ))}
    </div>
  );
};
